import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../config/api';
import { RootState } from '../config/store';

// --- TYPE DEFINITIONS ---

interface ErrorType {
  message: string;
}

// Define the shape of a Payment object
interface Payment {
  id: string;
  paidAmount: number;
  dueAmount: number;
  paymentDate: string;
  paymentMethod: string;
  orderId: string;
  status: number;
}

interface CreatePaymentPayload {
  paidAmount: number;
  dueAmount: number;
  paymentDate: string;
  paymentMethod: string;
  orderId: string;
  status: number;
}

interface UpdatePaymentPayload extends CreatePaymentPayload {
  id: string;
}

// --- ASYNC THUNKS (SIMPLIFIED) ---

export const createPayment = createAsyncThunk<Payment, CreatePaymentPayload>(
  'client/createPayment',
  async (paymentData, { rejectWithValue }) => {
    try {
      // The interceptor adds the token automatically
      const response = await api.post('/invoices', paymentData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const updatePayment = createAsyncThunk<Payment, UpdatePaymentPayload>(
  'users/updatePayment',
  async (paymentData, { rejectWithValue }) => {
    try {
      // The interceptor adds the token automatically
      const response = await api.put(`/invoices/${paymentData.id}`, paymentData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data);
    }
  }
);

// --- SLICE DEFINITION ---

interface BillingState {
  payment: Payment | null;
  error: any;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
}

const initialState: BillingState = {
  payment: null,
  error: null,
  status: 'idle',
};

const billingSlice = createSlice({
  name: 'billing',
  initialState,
  reducers: {},
  // ✅ FIX: Updated to the recommended builder syntax
  extraReducers: (builder) => {
    builder
      .addCase(createPayment.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createPayment.fulfilled, (state, action: PayloadAction<Payment>) => {
        state.status = 'succeeded';
        state.payment = action.payload;
        state.error = null;
      })
      .addCase(createPayment.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(updatePayment.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updatePayment.fulfilled, (state, action: PayloadAction<Payment>) => {
        state.status = 'succeeded';
        state.payment = action.payload;
        state.error = null;
      })
      .addCase(updatePayment.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });
  },
});

export default billingSlice.reducer;